import React from 'react'
import PropTypes from 'prop-types'
import { Paragraph, Separator, useContainerWidth, MarginBox } from '@fs/zion-ui'
import { css } from '@emotion/core'
import SectionHeading from './SectionHeading'
import SafeRichText from '../SafeRichText'
import { validFlavors } from '../../config'
import ExpanderGroup from '../ExpanderGroup'
import { MonoText } from '../type'

const containerCss = css`
  background: var(--image) no-repeat top center/cover;
  border-radius: 8px;
`

export default function SectionCard({
  overline,
  heading,
  subHeading,
  description,
  actions,
  flavor = 'flavorless',
  headingLevel = 'h2',
  headingSize = 'H3',
  image,
  graphic,
  children,
  disabled,
  // expander props
  expandable,
  expanderProps,

  ...props
}) {
  const atWidth = useContainerWidth()
  const marginSize = atWidth({ default: 'xs', lg: 'sm' })

  // if the children are interactive, like a TextField, then putting it in another component breaks functionality
  const content = (
    <>
      <MonoText>
        <SafeRichText as={Paragraph} dirtyInput={description} />
      </MonoText>
      {children}
      {!(description && children && graphic) && <Separator size="xs" />}
    </>
  )

  const headingProps = {
    heading,
    overline,
    subHeading,
    actions,
    flavor,
    size: headingSize,
    headingLevel,
    'aria-disabled': disabled,
  }

  return (
    <MarginBox
      marginX={marginSize}
      marginY={marginSize}
      css={[props.css, containerCss]}
      style={image && { '--image': `url(${image})` }}
      {...props}
    >
      {expandable ? (
        <ExpanderGroup headingProps={headingProps} {...expanderProps}>
          {content}
        </ExpanderGroup>
      ) : (
        <>
          <SectionHeading {...headingProps} />
          {content}
        </>
      )}
    </MarginBox>
  )
}

SectionCard.propTypes = {
  flavor: PropTypes.oneOf(validFlavors),
  image: PropTypes.string,
  overline: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  headingLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  subHeading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  action: PropTypes.node,
  graphic: PropTypes.string,
  scrim: PropTypes.bool,
  hideDivider: PropTypes.bool,
  children: PropTypes.node,
}
